import { useEffect, useState } from 'react';
import type { Bet, RoundSnapshot } from '@urbanrats/contracts';
import type { ClientGateways, PlayerStatsView } from '../gateway/index.ts';
import { useI18n, type MessageKey } from '../i18n/index.ts';
import { fmtAtomic, fmtMoney, fmtOdds } from '../lib/format.ts';
import { conditionText } from '../lib/gameText.ts';

interface Props {
  gw: ClientGateways;
  bets: Bet[];
  onEvidence: (gameRoundId: string) => void;
}

/** Per bet: round, condition, stake, odds, final count, return (gross/net), status, evidence. */
export function HistoryPanel({ gw, bets, onEvidence }: Props) {
  const { t, intl } = useI18n();
  const [rounds, setRounds] = useState<Map<string, RoundSnapshot>>(new Map());
  useEffect(() => {
    let live = true;
    void gw.game.listRecentRounds(80).then((rs) => live && setRounds(new Map(rs.map((r) => [r.gameRoundId, r]))));
    return () => {
      live = false;
    };
  }, [gw, bets]);

  const stats = gw.playerStats ? <MyStats load={gw.playerStats} bets={bets} /> : null;
  if (bets.length === 0)
    return (
      <>
        {stats}
        <p className="muted">{t('history.empty')}</p>
      </>
    );
  return (
    <>
      {stats}
      {gw.kind === 'demo' && <p className="note">{t('history.sessionOnly')}</p>}
      <ul className="history-list" data-testid="history-list">
        {bets.map((b) => {
          const r = rounds.get(b.gameRoundId);

          const count = r?.state === 'VOIDED' ? null : (r?.finalCount ?? null);
          // "Paid" only when the credit is final; a pending credit is shown as pending.
          const settled = b.state === 'SETTLED';
          return (
            <li key={b.betId} className={`history-item bs-${b.state}`} data-testid={`hist-${b.betId}`}>
              <div className="hi-row">
                <strong>
                  {t(`sel.${b.selection}` as MessageKey)}
                  {r && <span className="note"> · {conditionText(t, r.manifest.rules, b.selection)}</span>}
                </strong>
                <span className={`status-tag bs-${b.state}`}>{t(`bstate.${b.state}` as MessageKey)}</span>
              </div>
              <dl className="hi-grid">
                <dt>{t('history.col.round')}</dt>
                <dd title={b.gameRoundId}>
                  {b.gameRoundId.slice(-6)}
                  {r && <span className={`chip mode mode-${r.manifest.sourceMode} tiny`}>{t(`mode.${r.manifest.sourceMode}` as MessageKey)}</span>}
                </dd>
                <dt>{t('history.col.stake')}</dt>
                <dd>
                  {fmtMoney(b.stake, intl)}
                  {b.funding === 'FREEBET' && <span className="chip tiny">{t('history.freebet')}</span>}
                </dd>
                {b.funding === 'FREEBET' && (
                  <>
                    <dt>{t('history.cashDebit')}</dt>
                    <dd>{fmtMoney(b.cashDebit, intl)}</dd>
                  </>
                )}
                <dt>{t('history.col.odds')}</dt>
                <dd>
                  {b.quote ? fmtOdds(b.quote.odds, intl) : '—'}
                  {b.quote?.lightning && (
                    <span className="chip tiny lightning-chip">
                      {t('history.lightning', {
                        f: fmtOdds(b.quote.lightning, intl).replace('×', ''),
                      })}
                    </span>
                  )}
                  {b.quote && b.quote.boostBps > 0 && <span className="chip tiny">{t('history.boost', { pct: b.quote.boostBps / 100 })}</span>}
                </dd>
                <dt>{t('history.col.count')}</dt>
                <dd data-testid="hist-count">{count ?? '—'}</dd>
                <dt>{t('history.col.return')}</dt>
                <dd>
                  {b.payoutGross ? fmtMoney(b.payoutGross, intl) : '—'}
                  {b.payoutGross && !settled && <span className="note"> ({t('bstate.CREDIT_PENDING')})</span>}
                </dd>
                <dt>{t('history.col.net')}</dt>
                <dd>{b.payoutNet ? fmtMoney(b.payoutNet, intl) : b.state === 'VOIDED' ? t('history.refunded') : '—'}</dd>
              </dl>
              {r && (r.finalCount !== null || r.state === 'VOIDED') && (
                <button type="button" className="link-btn" onClick={() => onEvidence(b.gameRoundId)}>
                  {t('history.evidence')} →
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/** The player's own totals from the server (all sessions); money per currency, never summed across. */
function MyStats({ load, bets }: { load: () => Promise<PlayerStatsView | null>; bets: Bet[] }) {
  const { t, intl } = useI18n();
  const [s, setS] = useState<PlayerStatsView | null>(null);
  useEffect(() => {
    let live = true;
    void load().then((v) => live && setS(v));
    return () => {
      live = false;
    };
  }, [load, bets.length]);
  if (!s || s.totals.bets === 0) return null;
  const rate = Math.round((s.totals.wins / s.totals.bets) * 100);
  return (
    <section className="my-stats" data-testid="my-stats" aria-label={t('stats.title')}>
      <h3>{t('stats.title')}</h3>
      <div className="ms-grid">
        <div>
          <b>{s.totals.rounds}</b>
          <span>{t('stats.rounds')}</span>
        </div>
        <div>
          <b>{s.totals.bets}</b>
          <span>{t('stats.bets')}</span>
        </div>
        <div>
          <b>{rate}%</b>
          <span>{t('stats.winRate')}</span>
        </div>
        <div>
          <b>{s.totals.exactHits}</b>
          <span>{t('stats.exact')}</span>
        </div>
        <div>
          <b>{s.totals.bestMultiplier > 0 ? `×${s.totals.bestMultiplier.toLocaleString(intl, { maximumFractionDigits: 2 })}` : '—'}</b>
          <span>{t('stats.best')}</span>
        </div>
        <div>
          <b>{s.totals.playMinutes}</b>
          <span>{t('stats.minutes')}</span>
        </div>
      </div>
      {s.money.map((m) => (
        <p key={m.currency} className="note">
          {t('stats.money', {
            stake: fmtAtomic(BigInt(m.stake), m.scale, m.currency, intl),
            payout: fmtAtomic(BigInt(m.payout), m.scale, m.currency, intl),
            net: fmtAtomic(BigInt(m.net), m.scale, m.currency, intl),
          })}
        </p>
      ))}
      <p className="note">
        {t('stats.last30', {
          rounds: s.last30Days.rounds,
          wins: s.last30Days.wins,
        })}
        {s.favouriteSelection && (
          <>
            {' '}
            ·{' '}
            {t('stats.favourite', {
              sel: t(`sel.${s.favouriteSelection}` as MessageKey),
            })}
          </>
        )}
      </p>
    </section>
  );
}
